import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { firestore } from 'firebase';
import { map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class DataServiceService {
	public settingsDisplaySource: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public settingsDisplayCheck = this.settingsDisplaySource.asObservable();

	constructor(private readonly firestore: AngularFirestore) {}

	public getStudentData(studentId) {
		return this.firestore.collection('students').doc(studentId).valueChanges();
	}

	public getTemplate(templateId) {
		return this.firestore.collection('templates').doc(templateId).valueChanges();
	}

	public getCollection(collectionName) {
		return this.firestore.collection(collectionName).valueChanges();
	}

	public getData(studentId) {
		return this.firestore.collection('students').doc(studentId).get();
	}

	public updateExistingStudent(currentUser) {
		const userActivity = {
			status: 'visit',
			created_at: moment().format()
		};
		return this.firestore.collection('students').doc(currentUser.email).update({
			banner_data: currentUser.banner_data,
			updated_at: moment().format(),
			lastSignInTime: moment(currentUser.metadata.lastSignInTime).format(),
			user_activity: firestore.FieldValue.arrayUnion(userActivity)
		});
	}

	public getCollectionDocWithId(collectionName) {
		return this.firestore.collection(collectionName).get();
	}

	public getCollectionDocWithIdByWhere(collectionName, fieldName, queryOperator, searchValue) {
		return this.firestore
			.collection(collectionName, (ref) =>
				ref.where(fieldName, queryOperator, searchValue).where('published', '==', true)
			)
			.get();
	}

	public insertInto(collectionName, data) {
		return this.firestore.collection(collectionName).add(data);
	}

	public submaskid() {
		const student = this.firestore.collection('subtasks');
		return student.valueChanges();
	}

	public phasesFor(contentfulPhaseIds) {
		return this.firestore
			.collection('phases', (ref) =>
				ref.where('contentful_id', 'in', contentfulPhaseIds).where('published', '==', true)
			)
			.get()
			.toPromise();
	}

	public tasksFor(phase) {
		return this.firestore
			.collection('tasks', (ref) =>
				ref.where('contentful_id', 'in', phase.contentful_task_ids).where('published', '==', true)
			)
			.get()
			.toPromise();
	}

	public subtasksFor(task) {
		return this.firestore
			.collection('subtasks', (ref) =>
				ref.where('contentful_id', 'in', task.contentful_subtask_ids).where('published', '==', true)
			)
			.get()
			.toPromise();
	}

	public insertNewStudentData(student) {
		return this.firestore.collection('students').doc(student.upn).set(student);
	}
	/* highlight the todays tab */
	public setSettingsDisplay(actionFlag) {
		this.settingsDisplaySource.next(actionFlag);
		sessionStorage.setItem('showSettings', JSON.stringify(actionFlag));
	}

	// --------------------------------------Update subtasks in student-----------------------------------------------

	public subtaskUpdate(studentId, subTask) {
		this.getStudentData(studentId).subscribe((data) => {
			this.firestore.collection('students').doc(studentId).update({ user_subtasks_test: subTask });
		});
	}

	// ----------------------------------Faculty-----------------------------------------------------------

	public getFacultyDetails() {
		return this.firestore.collection('faculties').valueChanges();
	}

	public getSubtasks() {
		return this.firestore.collection('subtasks').get().toPromise();
	}

	public getStudentDataByDocToPromise(uid) {
		return this.firestore.collection('students').doc(uid).get().toPromise();
	}
	public updateStudentSubtask(studentUID, userSubtasks, currentTask, currentPhaseTask) {
		let currentTaskPyload;
		let updatePayload;

		if (currentTask && !currentPhaseTask) {
			currentTaskPyload = {
				status: 'locked',
				created_at: moment().format(),
				task_id: currentTask._id
			};
			updatePayload = {
				user_subtasks: userSubtasks,
				user_tasks: firestore.FieldValue.arrayUnion(currentTaskPyload)
			};
		} else if (currentTask && currentPhaseTask) {
			const nextTask = { ...currentPhaseTask.nextTask };
			const nextPhase = { ...currentPhaseTask.nextPhase };

			// delete un-neccessary property for phase
			delete nextPhase.tasks;
			delete nextPhase.currentPhase;
			delete nextPhase.is_complete;
			// delete un-neccessary property for task
			delete nextTask.subtasks;
			delete nextTask.currentTask;
			delete nextTask.isTaskLocked;
			delete nextTask.is_complete;
			delete nextTask.taskGoalMessage;

			currentTaskPyload = {
				status: 'locked',
				created_at: moment().format(),
				task_id: currentTask._id
			};

			updatePayload = {
				user_subtasks: userSubtasks,
				user_tasks: firestore.FieldValue.arrayUnion(currentTaskPyload),
				current_phase: nextPhase,
				current_task: nextTask
			};
		} else {
			updatePayload = {
				user_subtasks: userSubtasks
			};
		}
		return this.firestore.collection('students').doc(studentUID).update(updatePayload);
	}

	/**
   * getSnanshot Data to retrive the doc id as well
   * @param collectionName (string) its a collection name
   * @param serachField (string) firestore key field in that collection
   * @param condition (string) Firestore search condition
   * @param serachValue (any)
   */
	public getSnapshotOfCollectionDataByWhere(collectionName, serachField, condition, serachValue) {
		return this.firestore
			.collection(collectionName, (ref) => ref.where(serachField, condition, serachValue))
			.snapshotChanges()
			.pipe(
				map((actions) =>
					actions.map((snapshotData) => {
						let data = {};
						data = snapshotData.payload.doc.data();
						const _id = snapshotData.payload.doc.id;
						return { _id, ...data };
					})
				)
			);
	}

	/**
   * This function will return the doc id as _id and then doc.data()
   * @param collectionName (String) colection anme
   */
	public getSnashotOfColecctionData(collectionName) {
		return this.firestore.collection(collectionName).snapshotChanges().pipe(
			map((actions) =>
				actions.map((snapshotData) => {
					let data = {};
					data = snapshotData.payload.doc.data();
					const _id = snapshotData.payload.doc.id;
					return { _id, ...data };
				})
			)
		);
	}

	/**
   * @description Will update the studen doc
   * @param docId (String) Student Doc id
   * @param updatedObj (Object) Student updated object
   */
	public updateStudentData(docId, updatedObj) {
		if (updatedObj.preferred_email === undefined) {
			delete updatedObj.preferred_email;
		}
		if (updatedObj.phone_number === undefined) {
			delete updatedObj.phone_number;
		}

		return this.firestore.collection('students').doc(docId).update(updatedObj);
	}

	/**
  * @description Will update the studen user tasks
  * @param docId (String) Student Doc id
  * @param updatedArray (Array) Student updated user tasks array
  * @param current_phase_task (Object) Holding current phase and task Object
  */
	public updateStudentUserTasksAndCurrentPhaseTask(docId, updatedArray, currentPhaseTask, isGraduated) {
		if (currentPhaseTask && updatedArray) {
			const nextTask = { ...currentPhaseTask.nextTask };
			const nextPhase = { ...currentPhaseTask.nextPhase };

			// delete un-neccessary property for phase
			delete nextPhase.tasks;
			delete nextPhase.currentPhase;
			delete nextPhase.is_complete;
			// delete un-neccessary property for task
			delete nextTask.subtasks;
			delete nextTask.currentTask;
			delete nextTask.isTaskLocked;
			delete nextTask.is_complete;
			delete nextTask.taskGoalMessage;
			delete nextTask.isLastTask;

			const updatedObj = {
				user_tasks: updatedArray,
				canUncheck: false,
				current_phase: nextPhase,
				current_task: nextTask
			};

			if (isGraduated === true) {
				updatedObj['isGraduated'] = false;
			}

			return this.firestore.collection('students').doc(docId).update(updatedObj);
		}
	}
}
